import { useQuery } from "@tanstack/react-query";

import { IStandardFieldSetting } from "../../@types/StandardField";
import { standardFieldsService } from "../../services/standardFieldsService";
import { STANDARD_FIELD_DEFAULTS } from "../../utils/standardFields";

// The api instance's response interceptor resolves errors to `error.response` instead of
// rejecting, so a non-2xx call yields an ErrorResponse body rather than an array. Guard against
// that by falling back to the catalogue defaults — never to an empty list, which would blank
// every configurable field on the Add Patient form.
function asSettings(data: unknown): IStandardFieldSetting[] {
  return Array.isArray(data)
    ? (data as IStandardFieldSetting[])
    : STANDARD_FIELD_DEFAULTS;
}

// EN-5192: the built-in onboarding fields' settings for the currently selected ward set.
// Runs with no ward too — the API answers with the defaults, so the form renders while the
// staff member is still choosing a ward.
export function useStandardFieldsForWards(wardIds: number[]) {
  // Sorted so the key is stable regardless of ward selection order.
  const sortedWardIds = [...wardIds].sort((a, b) => a - b);

  const { data, isError, isFetching, isLoading } = useQuery<
    IStandardFieldSetting[]
  >({
    queryKey: ["standard-fields-for-wards", sortedWardIds],
    queryFn: async () => {
      const { data } = await standardFieldsService.getStandardFieldsForWards(
        sortedWardIds
      );

      return asSettings(data);
    },
  });

  return {
    // STANDARD_FIELD_DEFAULTS is a module-level constant, so the "not loaded yet" fallback keeps
    // a STABLE identity across renders. A `?? []` literal here would hand out a new array every
    // render and loop any consumer that has it in a useEffect dep array (see
    // hooks/queries/customFields.ts, where exactly that froze the Add Patient dropdowns).
    standardFields:
      (data as IStandardFieldSetting[]) ?? STANDARD_FIELD_DEFAULTS,
    isStandardFieldsFetching: isFetching,
    isStandardFieldsError: isError,
    isStandardFieldsLoading: isLoading,
  };
}
